import React, { useEffect, useState } from "react";

import { Button, IconButton, Menu, MenuItem } from "@mui/material";
import { MoreVert as MoreVertIcon, Done as DoneIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import { Folder } from "../../../../../models/folder";
import { NotificationToast } from "../../../Toast/NotificationToast";
import useStyles from "./styles";

type Props = {
  folder?: Folder;
  onOpen?: () => void;
  onDelete?: () => void;
  onEdit?: (folder: Folder) => void;
  isOnViewMode?: boolean;
  isDisabled?: boolean;
};

const FolderPage = (props: Props) => {
  const { folder, onOpen, onDelete, onEdit, isOnViewMode = false, isDisabled } = props;

  const { t: translate } = useTranslation();

  const [name, setName] = useState<string>();

  const [anchorMoreIcon, setAnchorMoreIcon] = useState<null | HTMLElement>(null);

  const [_isDisabled, setIsDisabled] = useState<boolean>(!!folder?.name);

  const classes = useStyles({ isDisabled: _isDisabled })();

  useEffect(() => {
    if (folder && folder?.name) setName(folder?.name);
  }, [folder]);

  useEffect(() => {
    if (isDisabled !== undefined) setIsDisabled(isDisabled);
  }, [isDisabled]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorMoreIcon(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorMoreIcon(null);
  };

  const handleEditName = () => {
    setIsDisabled(!_isDisabled);
  };

  const confirmChange = () => {
    if (name) {
      if (isDisabled === undefined) handleEditName();
      if (onEdit) onEdit({ name });
      setName(folder?.name || name);
    } else {
      NotificationToast.warning(translate("theFolderNameIsRequired"));
    }
  };

  const renderMenuIcon = () => {
    if (folder?.isAllowedDelete === false || folder?.isAllowedEdit === false) return null;

    if (onDelete && onEdit && !isOnViewMode) {
      return (
        <div>
          <IconButton
            onClick={handleOpenMenu}
            size="small"
            className={classes.iconButton}
            data-cy={`button-more-${folder?.name}`}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            id="long-menu"
            anchorEl={anchorMoreIcon}
            keepMounted
            open={Boolean(anchorMoreIcon)}
            onClose={handleCloseMenu}
          >
            <MenuItem
              key="Rename"
              data-cy={`button-rename-${folder?.name}`}
              onClick={() => {
                handleCloseMenu();
                handleEditName();
                if (!_isDisabled) setName(folder?.name);
              }}
            >
              <span>{translate("rename")}</span>
            </MenuItem>
            <MenuItem
              key="Delete"
              data-cy={`button-delete-${folder?.name}`}
              onClick={() => {
                handleCloseMenu();
                if (onDelete) onDelete();
              }}
            >
              <span className={classes.textDelete}>{translate("delete")}</span>
            </MenuItem>
          </Menu>
        </div>
      );
    }

    return null;
  };

  const renderIconButtonConfirmEdit = () => {
    if (!_isDisabled) {
      return (
        <IconButton onClick={confirmChange} size="small" data-cy="button-confirm-change">
          <DoneIcon fontSize="small" />
        </IconButton>
      );
    }

    return null;
  };
  const hasFiles = folder?.files && folder?.files?.length > 0;
  const hasFolders = folder?.folders && folder?.folders?.length > 0;

  return (
    <div className={classes.mainDiv}>
      <div className={hasFiles || hasFolders ? classes.headerFolderFill : classes.headerFolder} />
      {hasFiles || hasFolders ? <div className={classes.folderItem} /> : undefined}
      <Button
        disableElevation
        disableRipple
        disabled={!onOpen}
        onClick={onOpen}
        className={hasFiles || hasFolders ? classes.folderFillFront : classes.folder}
        data-cy={`button-open-${folder?.name}`}
      ></Button>
      {renderMenuIcon()}
      <div className={classes.containerInput}>
        <div style={{ width: _isDisabled ? "85%" : "70%" }}>
          <input
            name="folder"
            value={name}
            disabled={_isDisabled}
            className={classes.input}
            onChange={(event) => setName(event.target.value)}
            data-cy={folder?.name || name ? `input-folder-${folder?.name || name}` : "input-folder"}
          />
        </div>
        {renderIconButtonConfirmEdit()}
      </div>
    </div>
  );
};

export default FolderPage;
